/**
 * GuardCard
 *
 * Enhanced guard card component with patrol progress display.
 * Shows guard info, patrol status, elapsed time, and checkpoint progress.
 *
 * Visual Design:
 * ┌────────────────────────────────────┐
 * │  John Doe                    🔋 85%│
 * │  Downtown Office                   │
 * │  ▶ In Progress • 2h 15m            │
 * │  ████████░░░░ 6/10 checkpoints     │
 * │  📍 5 min ago                      │
 * └────────────────────────────────────┘
 */

import React from 'react';
import { Box, Typography, Tooltip, Link } from '@mui/material';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import styles from './GuardCard.module.css';
import {
  GuardLocation,
  PatrolStatus,
  STATUS_COLORS,
  PATROL_STATUS_COLORS,
  PATROL_STATUS_LABELS,
} from './types';
import classNames from 'classnames';

// ============================================
// CheckpointProgress Component
// ============================================

export interface CheckpointProgressProps {
  /** Number of completed checkpoints */
  completed: number;
  /** Total number of checkpoints */
  total: number;
  /** Compact mode for smaller displays */
  compact?: boolean;
}

/**
 * CheckpointProgress - Visual progress bar for checkpoint completion
 */
export const CheckpointProgress: React.FC<CheckpointProgressProps> = ({
  completed,
  total,
  compact = false,
}) => {
  // Don't render if no checkpoints
  if (total === 0) {
    return null;
  }

  const percentage = Math.round((completed / total) * 100);
  const isComplete = completed >= total;

  return (
    <Box className={classNames(styles.checkpointProgress, { [styles.compact]: compact })}>
      <Box className={styles.progressBarContainer}>
        <Box
          className={classNames(styles.progressBar, { [styles.complete]: isComplete })}
          style={{ width: `${percentage}%` }}
        />
      </Box>
      <Typography
        variant="caption"
        className={classNames(styles.checkpointText, { [styles.complete]: isComplete })}
      >
        {completed}/{total} checkpoints
      </Typography>
    </Box>
  );
};

// ============================================
// PatrolStatusBadge Component
// ============================================

interface PatrolStatusBadgeProps {
  status: PatrolStatus;
  elapsedTime?: string | null;
}

const PatrolStatusBadge: React.FC<PatrolStatusBadgeProps> = ({ status, elapsedTime }) => {
  const StatusIcon = status === 'in_progress' ? PlayArrowIcon :
                     status === 'completed' ? CheckCircleIcon :
                     AccessTimeIcon;

  return (
    <Box
      className={styles.patrolStatusBadge}
      style={{ '--patrol-color': PATROL_STATUS_COLORS[status] } as React.CSSProperties}
    >
      <StatusIcon className={styles.patrolStatusIcon} />
      <Typography variant="caption" className={styles.patrolStatusText}>
        {PATROL_STATUS_LABELS[status]}
      </Typography>
      {elapsedTime && (
        <>
          <span className={styles.patrolDivider}>•</span>
          <Typography variant="caption" className={styles.elapsedTime}>
            {elapsedTime}
          </Typography>
        </>
      )}
    </Box>
  );
};

// ============================================
// BatteryIndicator Component
// ============================================

interface BatteryIndicatorProps {
  percent: number | null;
}

const BatteryIndicator: React.FC<BatteryIndicatorProps> = ({ percent }) => {
  if (percent === null) return null;

  const isLow = percent < 20;
  const Icon = isLow ? BatteryAlertIcon : BatteryFullIcon;

  return (
    <Tooltip title={`Battery: ${percent}%`}>
      <Box className={classNames(styles.battery, { [styles.low]: isLow })}>
        <Icon className={styles.batteryIcon} />
        <span>{percent}%</span>
      </Box>
    </Tooltip>
  );
};

// ============================================
// GuardCard Component
// ============================================

export interface GuardCardProps {
  /** Guard data to display */
  guard: GuardLocation;
  /** Whether this card is selected */
  isSelected?: boolean;
  /** Click handler */
  onClick?: (guardId: number) => void;
  /** Whether to show patrol progress (if available) */
  showProgress?: boolean;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Callback when a site name is clicked to open site info */
  onSiteClick?: (addressId: number, siteName: string) => void;
}

/**
 * Formats elapsed time from start time to now
 */
function formatElapsedTime(startTime: string | null | undefined): string | null {
  if (!startTime) return null;

  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();

  if (diffMs < 0) return null;

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const remainingMinutes = diffMinutes % 60;

  if (diffHours === 0) {
    return `${diffMinutes}m`;
  }

  return `${diffHours}h ${remainingMinutes}m`;
}

const GuardCard: React.FC<GuardCardProps> = ({
  guard,
  isSelected = false,
  onClick,
  showProgress = true,
  compact = false,
  onSiteClick,
}) => {
  const patrol = guard.currentPatrol;
  const hasPatrol = !!patrol;
  const patrolStatus = patrol?.patrolStatus || (hasPatrol ? 'in_progress' : undefined);
  const elapsedTime = formatElapsedTime(patrol?.startedAt || patrol?.startTime);
  const hasCheckpoints = patrol &&
    patrol.checkpointsTotal !== undefined &&
    patrol.checkpointsTotal > 0;
  const isLowBattery = guard.batteryPercent !== null && guard.batteryPercent < 20;

  return (
    <Box
      className={classNames(styles.card, {
        [styles.selected]: isSelected,
        [styles.compact]: compact,
        [styles.clickable]: !!onClick,
        [styles.lowBattery]: isLowBattery,
      })}
      onClick={() => onClick?.(guard.guardId)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(guard.guardId);
        }
      }}
    >
      {/* Status indicator */}
      <Box
        className={styles.statusIndicator}
        style={{ backgroundColor: STATUS_COLORS[guard.status] }}
      />

      {/* Main content */}
      <Box className={styles.content}>
        {/* Header row: Name + Battery */}
        <Box className={styles.headerRow}>
          <Typography variant="body1" className={styles.guardName}>
            {guard.name}
          </Typography>
          <BatteryIndicator percent={guard.batteryPercent} />
        </Box>

        {/* Site/Customer name */}
        {hasPatrol ? (
          <Typography variant="body2" className={styles.siteName}>
            {patrol.addressId && onSiteClick ? (
              <Link
                component="button"
                variant="body2"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSiteClick(patrol.addressId!, patrol.siteName);
                }}
                className={styles.siteNameLink}
              >
                {patrol.siteName || patrol.customerName}
              </Link>
            ) : (
              patrol.siteName || patrol.customerName
            )}
          </Typography>
        ) : (
          <Typography variant="body2" className={styles.noPatrol}>
            Not on patrol
          </Typography>
        )}

        {/* Patrol status badge with elapsed time */}
        {hasPatrol && patrolStatus && (
          <PatrolStatusBadge status={patrolStatus} elapsedTime={elapsedTime} />
        )}

        {/* Checkpoint progress bar */}
        {showProgress && hasCheckpoints && (
          <CheckpointProgress
            completed={patrol.checkpointsCompleted || 0}
            total={patrol.checkpointsTotal || 0}
            compact={compact}
          />
        )}

        {/* Last seen timestamp */}
        <Box className={styles.lastSeen}>
          <LocationOnIcon className={styles.lastSeenIcon} />
          <Typography variant="caption" className={styles.lastSeenText}>
            {guard.lastSeenFormatted}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GuardCard;
